import React, { useCallback, useMemo, useEffect } from 'react';
import {
  AccountTab,
  AttributeTab,
  BasicInformationTab,
  GeneralTab,
  ItemPriceTab,
  OtherTab,
  UnitConversionTab
} from './tabs';
import { initialVatTuSanPhamValues, vatTuSanPhamSchema, type VatTuSanPhamFormValues } from '../../schema';
import { getFormActionButtons, AritoFormV2, DialogV2, AritoIcon } from '@/components/custom/erp';
import UpdatePriceDialog from '../../../../ban-hang/gia-ban/components/update-price-dialog';
import type { GiaBan, GiaBanInput, DonViTinhPhu, VatTuSanPhamInput } from '@/types/schemas';
import { useVatTuSanPhamContext, VatTuSanPhamContext } from '../../context';
import { useFormFieldState, useUpdatePriceDialog } from '../../hooks';
import { useCRUD, useInputTableRows, useDonViTinh } from '@/hooks';
import type { GridEventListener } from '@/components/custom/erp';
import { extractApiErrorMessage } from '@/lib/error-utils';
import { QUERY_KEYS } from '@/constants/query-keys';
import type { FormMode } from '@/types/form';
import { toast } from '@/hooks/use-toast';

const FormDialogInner: React.FC = () => {
  const {
    formMode,
    selectedObj,
    detail,
    isCopyMode,
    handleCloseForm,
    addItem: addItemContext,
    updateItem: updateItemContext,
    clearSelection,
    handleAddClick,
    handleEditClick,
    handleDeleteClick,
    handleCopyClick
  } = useVatTuSanPhamContext();

  const initialData = formMode === 'add' && !isCopyMode ? undefined : detail;

  const { state, actions } = useFormFieldState(initialData ?? undefined);

  const {
    updatePriceDialogOpen,
    priceMode,
    selectedPriceData,
    openAddDialog,
    openEditDialog,
    closeDialog,
    setSelectedPriceData
  } = useUpdatePriceDialog();

  const {
    rows: unitConversionRows,
    selectedRowUuid: unitConversionSelectedRowUuid,
    handleRowClick: handleUnitConversionRowClick,
    handleAddRow: handleAddUnitConversionRow,
    handleDeleteRow: handleDeleteUnitConversionRow,
    handleCopyRow: handleCopyUnitConversionRow,
    handlePasteRow: handlePasteUnitConversionRow,
    handleMoveRow: handleMoveUnitConversionRow,
    handleCellValueChange: handleUnitConversionCellValueChange
  } = useInputTableRows<DonViTinhPhu>(initialData?.quy_doi_dvt_data || []);

  const {
    rows: itemPriceRows,
    setRows: setItemPriceRows,
    selectedRowUuid: itemPriceSelectedRowUuid,
    handleRowClick: handleItemPriceRowClickOriginal,
    handleAddRow: handleAddItemPriceRowOriginal,
    handleDeleteRow: handleDeleteItemPriceRowOriginal,
    handleCopyRow: handleEditItemPriceRowOriginal,
    handlePasteRow: handlePasteItemPriceRow,
    handleMoveRow: handleMoveItemPriceRow
  } = useInputTableRows<GiaBan>((initialData?.gia_ban_data || []) as unknown as GiaBan[]);

  const { addItem, updateItem, deleteItem } = useCRUD<GiaBan, GiaBanInput>({
    endpoint: QUERY_KEYS.GIA_BAN,
    initialFetch: false
  });

  // Wrap row click to also set full selected row for UpdatePriceDialog initialData
  const handleItemPriceRowClick: GridEventListener<'rowClick'> = useCallback(
    (params, event, details) => {
      handleItemPriceRowClickOriginal(params, event, details);
      const rowData = (params as { row?: GiaBan }).row || null;
      if (rowData) {
        setSelectedPriceData(rowData);
      }
    },
    [handleItemPriceRowClickOriginal, setSelectedPriceData]
  );

  // Custom handlers for UpdatePriceDialog
  const handleAddItemPriceRow = useCallback(() => {
    openAddDialog();
  }, [openAddDialog]);

  const handleEditItemPriceRow = useCallback(() => {
    if (itemPriceSelectedRowUuid) {
      const selectedRow = itemPriceRows.find(row => row.uuid === itemPriceSelectedRowUuid);
      if (selectedRow) openEditDialog(selectedRow);
    }
  }, [itemPriceRows, itemPriceSelectedRowUuid, openEditDialog]);

  // Delete handler: call API then remove from table
  const handleDeleteItemPriceRow = useCallback(async () => {
    try {
      const targetUuid = selectedPriceData?.uuid || itemPriceSelectedRowUuid;
      if (!targetUuid) return;
      await deleteItem(targetUuid);
      // Update local table state via original hook handler
      await handleDeleteItemPriceRowOriginal();
      // Ensure rows synced
      setItemPriceRows(itemPriceRows.filter(row => row.uuid !== targetUuid));
    } catch (err) {
      return;
    }
  }, [
    deleteItem,
    handleDeleteItemPriceRowOriginal,
    itemPriceRows,
    itemPriceSelectedRowUuid,
    selectedPriceData?.uuid,
    setItemPriceRows
  ]);

  const handleSubmit = async (data: VatTuSanPhamFormValues) => {
    try {
      const quy_doi_dvt = unitConversionRows.map(row => ({
        dvt: row.dvt_data?.uuid,
        he_so: row.he_so ?? 1
      }));
      const mergedData = { ...data, ...actions.mergeData(), quy_doi_dvt } as unknown as VatTuSanPhamInput;
      if (!mergedData.dvt) throw new Error('Hãy chọn đơn vị tính');
      if (!mergedData.ma_lvt) throw new Error('Hãy chọn loại vật tư');

      if (formMode === 'add') await addItemContext(mergedData);
      if (formMode === 'edit' && selectedObj) await updateItemContext(selectedObj.uuid, mergedData);

      handleCloseForm();
      clearSelection();
    } catch (err) {
      toast({
        description: extractApiErrorMessage({ data: err }),
        variant: 'destructive'
      });
    }
  };

  const handleSubmitGiaBan = async (data: GiaBanInput) => {
    try {
      if (priceMode === 'add') {
        const created = await addItem(data);
        const createdWithData = created as GiaBan & { data?: GiaBan };
        const createdRow = (created && (createdWithData.data || created)) || (data as unknown as GiaBan);
        setItemPriceRows([...itemPriceRows, createdRow]);
      } else if (priceMode === 'edit' && selectedPriceData && initialData) {
        const updated = await updateItem(selectedPriceData.uuid, { ...data, ma_vt: initialData.uuid } as GiaBanInput);
        const updatedWithData = updated as GiaBan & { data?: GiaBan };
        const updatedRow = (updated && (updatedWithData.data || updated)) || { ...selectedPriceData, ...data };
        setItemPriceRows(itemPriceRows.map(row => (row.uuid === selectedPriceData.uuid ? updatedRow : row)));
      }
      closeDialog();
    } catch (_err: unknown) {
      return;
    }
  };
  const formTabs = useMemo(
    () => [
      {
        id: 'thong_tin_chung',
        label: 'Thông tin chung',
        component: <GeneralTab formMode={formMode} state={state} actions={actions} />
      },
      {
        id: 'tai_khoan',
        label: 'Tài khoản',
        component: <AccountTab formMode={formMode} state={state} actions={actions} />
      },
      {
        id: 'qddvt',
        label: 'Quy đổi đvt',
        component: (
          <UnitConversionTab
            mode={formMode}
            rows={unitConversionRows}
            selectedRowUuid={unitConversionSelectedRowUuid}
            onRowClick={handleUnitConversionRowClick}
            onAddRow={handleAddUnitConversionRow}
            onDeleteRow={handleDeleteUnitConversionRow}
            onCopyRow={handleCopyUnitConversionRow}
            onPasteRow={handlePasteUnitConversionRow}
            onMoveRow={handleMoveUnitConversionRow}
            onCellValueChange={handleUnitConversionCellValueChange}
            dvt={state.donViTinh}
          />
        )
      },
      {
        id: 'gia_ban',
        label: 'Giá bán',
        component: (
          <ItemPriceTab
            mode={formMode}
            rows={itemPriceRows}
            selectedRowUuid={itemPriceSelectedRowUuid}
            onRowClick={handleItemPriceRowClick}
            onAddRow={handleAddItemPriceRow}
            onDeleteRow={handleDeleteItemPriceRow}
            onEditRow={handleEditItemPriceRow}
            onPasteRow={handlePasteItemPriceRow}
            onMoveRow={handleMoveItemPriceRow}
          />
        )
      },
      {
        id: 'thuoc_tinh',
        label: 'Thuộc tính',
        component: <AttributeTab formMode={formMode} state={state} actions={actions} />
      },
      {
        id: 'khac',
        label: 'Khác',
        component: <OtherTab formMode={formMode} boPhan={state.boPhan} setboPhan={actions.setboPhan} />
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formMode,
      state,
      actions,
      unitConversionRows,
      unitConversionSelectedRowUuid,
      handleUnitConversionRowClick,
      handleAddUnitConversionRow,
      handleDeleteUnitConversionRow,
      handleCopyUnitConversionRow,
      handlePasteUnitConversionRow,
      handleMoveUnitConversionRow,
      handleUnitConversionCellValueChange,
      itemPriceRows,
      itemPriceSelectedRowUuid,
      handleItemPriceRowClick,
      handleAddItemPriceRow,
      handleDeleteItemPriceRow,
      handleEditItemPriceRow,
      handlePasteItemPriceRow,
      handleMoveItemPriceRow
    ]
  );

  const actionButtons = getFormActionButtons(formMode, {
    onAdd: handleAddClick,
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
    onCopy: handleCopyClick,
    handleClose: handleCloseForm
  });
  return (
    <div className='h-full flex-1 lg:overflow-hidden'>
      <AritoFormV2
        mode={formMode}
        title={formMode === 'add' ? 'Mới' : formMode === 'edit' ? 'Sửa' : 'Xem'}
        schema={vatTuSanPhamSchema}
        actionButtons={actionButtons}
        initialData={initialData || initialVatTuSanPhamValues}
        onSubmit={handleSubmit}
        headerFields={<BasicInformationTab formMode={formMode} />}
        tabs={formTabs}
        onClose={handleCloseForm}
        subTitle={'Vật tư sản phẩm'}
      />
      {updatePriceDialogOpen && (
        <UpdatePriceDialog
          mode={priceMode}
          open={updatePriceDialogOpen}
          onClose={closeDialog}
          onSubmit={handleSubmitGiaBan}
          initialData={{
            ...selectedPriceData,
            ma_vt_data: {
              uuid: initialData?.uuid,
              ma_vt: initialData?.ma_vt,
              ten_vt: initialData?.ten_vt
            },
            dvt_data: {
              uuid: state.donViTinh?.uuid,
              ten_dvt: state.donViTinh?.ten_dvt,
              dvt: state.donViTinh?.dvt
            },
            status: '1'
          }}
        />
      )}
    </div>
  );
};

export const FormDialog = FormDialogInner;

interface FormDialogWrapperProps {
  open?: boolean;
  mode?: FormMode;
  formMode?: FormMode;
  initialData?: any;
  onClose?: () => void;
  onSubmit?: (data: any) => void;
  zIndex?: number;
}

const FormDialogWrapper = ({
  open,
  mode,
  formMode,
  initialData,
  onClose,
  onSubmit,
  zIndex
}: FormDialogWrapperProps) => {
  if (open === undefined) {
    return <FormDialogInner />;
  }

  const resolvedMode = mode || formMode || 'add';
  const { donViTinhs, fetchDonViTinhs } = useDonViTinh();

  useEffect(() => {
    if (open) {
      fetchDonViTinhs();
    }
  }, [open, fetchDonViTinhs]);

  const mockContextValue = useMemo(
    () => ({
      formMode: resolvedMode,
      selectedObj: initialData || null,
      detail: initialData || null,
      isCopyMode: false,
      handleCloseForm: onClose || (() => {}),
      addItem: async (data: any) => {
        await onSubmit?.(data);
        return {} as any;
      },
      updateItem: async (id: string, data: any) => {
        await onSubmit?.(data);
        return {} as any;
      },
      deleteItem: async () => {},
      refreshData: async () => {},
      isLoading: false,
      data: [],
      currentPage: 0,
      totalItems: 0,
      handlePageChange: async () => {},
      searchState: { filterParams: {}, searchResults: [], isSearching: false },
      handleSearchChange: () => {},
      hasFilterParams: false,
      tables: [],
      clearSelection: () => {},
      donViTinhs,
      showForm: true,
      showDelete: false,
      selectedRowIndex: null,
      handleRowClick: () => {},
      handleRefreshClick: () => {},
      handleRowDoubleClick: () => {},
      handleTabChange: () => {},
      showBulkDelete: false,
      handleCloseBulkDelete: () => {},
      isImportDialogOpen: false,
      setIsImportDialogOpen: () => {},
      handleOpenImportExcel: () => {},
      handleCloseDelete: () => {},
      handleAddClick: () => {},
      handleEditClick: () => {},
      handleViewClick: () => {},
      handleDeleteClick: () => {},
      handleCopyClick: () => {}
    }),
    [resolvedMode, initialData, onClose, onSubmit, donViTinhs]
  );

  if (!open) return null;

  return (
    <DialogV2
      open={open}
      onClose={onClose || (() => {})}
      title={
        resolvedMode === 'add'
          ? 'Thêm mới vật tư, sản phẩm'
          : resolvedMode === 'edit'
            ? 'Sửa vật tư, sản phẩm'
            : 'Xem chi tiết'
      }
      maxWidth='xl'
      disableBackdropClose={true}
      disableEscapeKeyDown={true}
      titleIcon={<AritoIcon icon={281} />}
      zIndex={zIndex}
      paperClassName='w-[min(1100px,calc(100vw-16px))]'
    >
      <VatTuSanPhamContext.Provider value={mockContextValue as any}>
        <div className='h-[min(650px,calc(100vh-150px))] overflow-y-auto p-4'>
          <FormDialogInner />
        </div>
      </VatTuSanPhamContext.Provider>
    </DialogV2>
  );
};

export default FormDialogWrapper;
